import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getGetPlanManagementQueryOptions } from '@/api/gen/quiz-plans/quiz-plans.gen';
import { useUser } from '@/lib/auth';
import { getInquiryLinkByCountryCode } from '@/module/plan/consts/inquiryLinks';
import { openPlanChangeInquiryAlert } from '@/shared/utils/planChangeInquiryAlert';

import { useExtensionBlockedAlert } from './useExtensionBlockedAlert';
import { useCountryCode } from './';

/**
 * 진입점이 많아 같은 모달이 자주 열린다. 매번 재조회하지 않도록 짧게 캐시한다.
 * 이 값이 좀 지나도 안전한 이유는 체크아웃이 진입 시 다시 검증하기 때문이다.
 */
const PLAN_MANAGEMENT_STALE_TIME = 30_000;

/**
 * 모듈 스코프에 한 번만 만든다. 요청을 보내지 않고 queryKey·queryFn 만 담은
 * 서술이라 재사용해도 안전하다.
 *
 * 이 조회는 **best-effort** 다. 실패해도 화면에 영향을 주면 안 되므로 전역 기본값을
 * 세 군데 덮는다.
 * - `meta.ignoreError`: 전역 `queryCache.onError`(`_app.page.tsx`)는 `useApiError`
 *   를 부르고, 그쪽은 401 에서 세션을 지우고 `/login` 으로 보낸다. 사용자 조작 없이
 *   뜨는 모달(LMS 자동 노출 등)의 배경 조회가 로그아웃을 유발하면 안 된다.
 * - `throwOnError`: 전역 기본이 `true` 라 같은 키를 구독하는 쪽으로 에러가 번질 수 있다.
 * - `retry`: 배경 조회가 실패하면 조용히 포기하고 체크아웃 가드에 맡긴다.
 */
const planManagementQuery = {
  ...getGetPlanManagementQueryOptions(),
  staleTime: PLAN_MANAGEMENT_STALE_TIME,
  meta: { ignoreError: true },
  throwOnError: false,
  retry: false,
};

/**
 * 활성 플랜 연장·업그레이드 진입을 막는 가드. 결제 화면 밖(업셀링 모달 등)에서 쓴다.
 *
 * 반환된 함수는 **동기**다. 변경 CTA 는 새 탭을 여는데, 비동기 확인을 거치면
 * 사용자 제스처를 벗어나 팝업 차단기에 막히기 때문이다. 그래서 판정에 필요한
 * 플랜 관리 응답은 `enabled` 인 동안 미리 받아 두고, 클릭 시점에는 캐시만 읽는다.
 *
 * 프리페치가 아직 안 끝났으면 차단하지 않고 통과시킨다. 체크아웃과 서버 정책이
 * 최종 방어선이다.
 *
 * @param enabled 변경 CTA 가 나올 수 있는 상태에서만 켠다. 불필요한 조회 방지.
 */
export const useCurrentPlanChangeGuard = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const queryClient = useQueryClient();
  const showExtensionBlockedAlert = useExtensionBlockedAlert();
  const { user } = useUser();
  const { countryCode } = useCountryCode();

  useEffect(() => {
    if (!enabled) return;
    void queryClient.prefetchQuery(planManagementQuery);
  }, [enabled, queryClient]);

  /** 차단이면 안내를 띄우고 `true`. 호출부는 `true` 일 때 진입을 중단한다. */
  return useCallback(
    (action: 'extend' | 'upgrade' = 'extend') => {
      const actions = queryClient.getQueryData(planManagementQuery.queryKey)
        ?.data.actions;

      if (action === 'extend') return showExtensionBlockedAlert(actions);

      if (
        actions?.upgradeBlockedReason !== 'SELF_SERVICE_ONLY' &&
        actions?.upgradeBlockedReason !== 'NOT_DIRECT_PURCHASE'
      ) {
        return false;
      }

      void openPlanChangeInquiryAlert(() => {
        window.open(
          getInquiryLinkByCountryCode(
            user?.countryCode ?? countryCode,
            'payment',
          ),
          '_blank',
          'noopener,noreferrer',
        );
      });
      return true;
    },
    [countryCode, queryClient, showExtensionBlockedAlert, user?.countryCode],
  );
};
